import { InventoryPosition, Zero } from 'nftx.js';
import { useTranslations } from 'use-intl';
import Button from '../../elements/Button';
import VaultIcon from '../../elements/VaultIcon';
import FormattedEther from '../../elements/FormattedEther';
import InventorySvg from '../../elements/InventorySvg';
import Icon, { Icons } from '../../elements/Icon';

export interface ManageInventoryPositionProps {
  symbol: string;
  position: InventoryPosition;
  vaultId: string;
  onClaim: () => void;
  onWithdraw: () => void;
}

export const ManageInventoryPosition = ({
  position: { claimableRewards, vToken, tokenId },
  symbol,
  vaultId,
  onClaim,
  onWithdraw,
}: ManageInventoryPositionProps) => {
  const t = useTranslations('ManageInventoryPosition');
  const isOpen = vToken > Zero;

  return (
    <div className="p-6 bg-gray-50">
      <header className="flex items-center mt-2">
        <h1 className="font-black text-lg mr-4">{symbol}</h1>
      </header>
      <section className="flex my-4 items-start">
        <InventorySvg
          tokenId={tokenId}
          alt={symbol}
          className="hidden sm:block w-2/5 flex-none mr-4"
        />
        <div className="space-y-4 flex-1">
          <div className="bg-gray-100 border border-gray-300 rounded-2xl p-2">
            <header className="flex mb-4">
              <h3 className="text-lg font-normal p-2">{t('inventory')}</h3>
              {isOpen && (
                <aside className="ml-auto flex items-center space-x-4 px-2">
                  <button
                    type="button"
                    className="text-xs text-pink-500 hover:underline"
                    onClick={onWithdraw}
                  >
                    {t('remove')}
                  </button>
                  <Button
                    variant="tertiary"
                    size="small"
                    disabled
                    title="Coming soon"
                  >
                    {t('add')}
                  </Button>
                </aside>
              )}
            </header>
            <dl className="p-4 rounded-lg border border-gray-300 bg-gray-50 space-y-2">
              <div className="flex justify-between">
                <dt className="flex items-center font-bold">
                  <VaultIcon vaultId={vaultId} className="mr-4 w-5" />
                  {symbol}
                </dt>
                <dd className="font-normal text-lg">
                  <FormattedEther value={vToken} />
                </dd>
              </div>
            </dl>
          </div>
          <div className="bg-gray-100 border border-gray-300 rounded-2xl p-2">
            <header className="flex mb-4">
              <h3 className="text-lg font-normal p-2">{t('unclaimedFees')}</h3>
              {!!claimableRewards && (
                <aside className="ml-auto flex items-center space-x-4 px-2">
                  <Button size="small" onClick={onClaim}>
                    {t('claim')}
                  </Button>
                </aside>
              )}
            </header>
            <dl className="p-4 rounded-lg border border-gray-300 bg-gray-50 space-y-2">
              <div className="flex justify-between">
                <dt className="flex items-center font-bold">
                  <Icon name={Icons.ETH} className="mr-4 w-5" />
                  {'ETH'}
                </dt>
                <dd className="font-normal text-lg">
                  <FormattedEther
                    value={claimableRewards}
                    maximumFractionDigits={4}
                  />
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ManageInventoryPosition;
